import {
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { features } from '../../config/features';
import { IAddressBase } from '../../models/address';
import { IFeatureGroupBase } from '../../models/features';
import { IUserAssignmentBase, IUserBase } from '../../models/users';
import {
  ITitularUser,
  showTitularUsersService,
} from '../../services/Users/ShowTitularUsersService';
import AddressForm, { addressValidation } from '../AddressForm';
import FiscalRecipientInfo from '../FiscalRecipientInfo';
import { DatePicker } from '../Form/DatePicker';
import { MaskedInput } from '../Form/MaskedInput';
import { ReactSelect } from '../Form/ReactSelect';

const cardBrandSelectOptions = [
  {
    label: 'Visa',
    value: 'visa',
  },
  {
    label: 'Mastercard',
    value: 'mastercard',
  },
  {
    label: 'Elo',
    value: 'elo',
  },
  {
    label: 'American Express',
    value: 'american_express',
  },
  {
    label: 'Hipercard',
    value: 'hipercard',
  },
  {
    label: 'Diners Club',
    value: 'diners_club',
  },
  {
    label: 'Cabal',
    value: 'cabal',
  },
  {
    label: 'Sorocred',
    value: 'sorocred',
  },
];

const cardBrandValues = cardBrandSelectOptions.map(({ value }) => value);

const sanitizeNumericValue = (value: string): string =>
  value.replace(/\D/g, '');

const sanitizeAlphaNumericValue = (value: string): string =>
  value.replace(/[^a-zA-Z0-9]/g, '');

export interface IBookingPayment {
  address?: IAddressBase;
  acquirerName?: string;
  acquirerNsu?: string;
  authorizationCode?: string;
  authorizedAt?: Date;
  brand?: string;
  userFiscalRecipient: boolean;
  installments?: number;
  type?: 'CREDIT_CARD' | 'DEBIT_CARD' | 'PIX' | 'CASH';
}

export interface IBookingPaymentModalFormData {
  address?: IAddressBase;
  acquirerName?: string;
  acquirerNsu?: string;
  authorizationCode?: string;
  authorizedAt?: Date;
  brand?: string;
  installments?: number;
  type?: 'CREDIT_CARD' | 'DEBIT_CARD' | 'PIX' | 'CASH';
  payAfter: boolean;
}

interface IBookingPaymentModalUserAssignment extends IUserAssignmentBase {
  featureGroup: IFeatureGroupBase;
}

interface IBookingPaymentModalUser extends IUserBase {
  userAssignments: IBookingPaymentModalUserAssignment[];
}

interface IBookingPaymentConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data?: IBookingPayment) => void;
  payAfterDisabled?: boolean;
  paymentData?: IBookingPayment;
  user: IBookingPaymentModalUser;
}

export const BookingPaymentConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  paymentData,
  payAfterDisabled = true,
  user,
}: IBookingPaymentConfirmationModalProps): JSX.Element => {
  const [payAfter, setPayAfter] = useState(false);
  const [fiscalRecipient, setFiscalRecipient] = useState<ITitularUser>();
  const [titularUser, setTitularUser] = useState<ITitularUser>();
  const showPaymentDetailsFields = features.productsServicesEnabled;
  const effectivePayAfterDisabled = showPaymentDetailsFields
    ? payAfterDisabled
    : false;

  const paymentFormSchema = useMemo(() => {
    if (!showPaymentDetailsFields) {
      return Yup.object({
        payAfter: Yup.boolean().default(true),
      });
    }

    return Yup.object({
      address: !fiscalRecipient?.address
        ? addressValidation.required() // Obrigatório se showAddressForm for true
        : Yup.object().nullable().notRequired().default(null), // Opcional se for false
      acquirerName: Yup.string()
        .when(['type', 'payAfter'], {
          is: (value: string, after: boolean) =>
            !after && ['CREDIT_CARD', 'DEBIT_CARD'].includes(value),
          then: Yup.string().required('Requerido'),
          otherwise: Yup.string().nullable().notRequired(),
        })
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        ),
      acquirerNsu: Yup.string()
        .when(['type', 'payAfter'], {
          is: (value: string, after: boolean) =>
            !after && ['CREDIT_CARD', 'DEBIT_CARD'].includes(value),
          then: Yup.string().required('Requerido'),
          otherwise: Yup.string().nullable().notRequired(),
        })
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        )
        .matches(/^\d*$/, 'Informe apenas números'),
      authorizationCode: Yup.string()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        )
        .matches(/^[a-zA-Z0-9]*$/, 'Informe apenas letras e números')
        .when(['type', 'payAfter'], {
          is: (type: string, after: boolean) => !after && type !== 'CASH',
          then: Yup.string().required('Requerido'),
          otherwise: Yup.string().nullable().notRequired(),
        }),
      authorizedAt: Yup.date()
        .nullable()
        .when(['type', 'payAfter'], {
          is: (type: string, after: boolean) =>
            !after && ['PIX', 'CASH'].includes(type),
          then: Yup.date().nullable().required('Requerido'),
          otherwise: Yup.date().nullable().notRequired(),
        }),
      brand: Yup.string().when('payAfter', {
        is: true,
        then: Yup.string()
          .oneOf([...cardBrandValues, null], 'Bandeira inválida')
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          ),
        otherwise: Yup.string().when('type', {
          is: (value: string) => ['CREDIT_CARD', 'DEBIT_CARD'].includes(value),
          then: Yup.string()
            .oneOf([...cardBrandValues, null], 'Bandeira inválida')
            .nullable()
            .transform((value, originalValue) =>
              originalValue === '' ? null : value,
            )
            .required('Requerido'),
          otherwise: Yup.string()
            .oneOf([...cardBrandValues, null], 'Bandeira inválida')
            .nullable()
            .transform((value, originalValue) =>
              originalValue === '' ? null : value,
            ),
        }),
      }),
      type: Yup.string().when('payAfter', {
        is: true,
        then: Yup.string()
          .oneOf(
            ['CREDIT_CARD', 'DEBIT_CARD', 'PIX', 'CASH', null],
            'Tipo inválido',
          )
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          ),
        otherwise: Yup.string()
          .oneOf(
            ['CREDIT_CARD', 'DEBIT_CARD', 'PIX', 'CASH', null],
            'Tipo inválido',
          )
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          )
          .required('Requerido'),
      }),
      payAfter: Yup.boolean().default(false),
    });
  }, [fiscalRecipient?.address, showPaymentDetailsFields]);

  const formMethods = useForm({
    resolver: yupResolver(paymentFormSchema),
    context: { payAfter },
  });

  const { control, register, formState, handleSubmit, reset, setValue, watch } =
    formMethods;

  const { errors } = formState;

  const selectedPaymentType = watch('type');

  const isPixPayment = selectedPaymentType === 'PIX';
  const isCashPayment = selectedPaymentType === 'CASH';
  const isCardPayment = ['CREDIT_CARD', 'DEBIT_CARD'].includes(
    selectedPaymentType,
  );

  useEffect(() => {
    if (!showPaymentDetailsFields && isOpen) {
      setPayAfter(true);
    }
  }, [isOpen, showPaymentDetailsFields]);

  const isGuestUser = useMemo(
    () => !!user?.userAssignments[0]?.featureGroup.key.includes('GUEST'),
    [user?.userAssignments],
  );

  const isLodgerUser = useMemo(
    () => !!user?.userAssignments[0]?.featureGroup.key.includes('LODGER'),
    [user?.userAssignments],
  );

  const userFiscalRecipient = useMemo(
    () => titularUser?.id === user.id || fiscalRecipient?.id === user.id,
    [fiscalRecipient?.id, titularUser?.id, user.id],
  );

  useEffect(() => {
    if (!showPaymentDetailsFields) {
      return;
    }

    if (isPixPayment) {
      setValue('acquirerNsu', null);
      setValue('acquirerName', null);
      setValue('brand', null);
    }

    if (isCardPayment) {
      setValue('authorizedAt', null);
    }

    if (isCashPayment) {
      setValue('authorizationCode', null);
    }
  }, [
    isCardPayment,
    isCashPayment,
    isPixPayment,
    showPaymentDetailsFields,
    reset,
    setValue,
  ]);

  useEffect(() => {
    if (!showPaymentDetailsFields) {
      return;
    }

    if (paymentData && !!isOpen) {
      reset({
        address: paymentData.address,
        authorizationCode: paymentData.authorizationCode,
        authorizedAt: paymentData.authorizedAt,
        type: paymentData.type,
        acquirerNsu: paymentData.acquirerNsu,
        acquirerName: paymentData.acquirerName,
        brand: paymentData.brand,
      });
    }
  }, [isOpen, paymentData, showPaymentDetailsFields, reset]);

  useEffect(() => {
    if (!isOpen) {
      reset({
        address: null,
        authorizationCode: null,
        authorizedAt: null,
        type: null,
        acquirerNsu: null,
        acquirerName: null,
        brand: null,
      });

      setPayAfter(false);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!showPaymentDetailsFields) {
      return;
    }

    async function loadTitular(): Promise<void> {
      const titular = await showTitularUsersService(user.id);

      setTitularUser(titular);
      setFiscalRecipient(titular);
    }

    loadTitular();
  }, [showPaymentDetailsFields, user.id, user?.userAssignments]);

  useEffect(() => {
    setValue('payAfter', payAfter);

    if (showPaymentDetailsFields) {
      setValue('acquirerName', payAfter ? null : 'getnet');
    }
  }, [payAfter, showPaymentDetailsFields, setValue]);

  const acquirerSelectOptions = useMemo(
    () => [
      {
        label: 'GetNet',
        value: 'getnet',
      },
    ],
    [],
  );

  const paymentTypeSelectOptions = useMemo(
    () => [
      {
        label: 'Crédito',
        value: 'CREDIT_CARD',
      },
      { label: 'Débito', value: 'DEBIT_CARD' },
      {
        label: 'Pix',
        value: 'PIX',
      },
      { label: 'Espécie (dinheiro)', value: 'CASH' },
    ],
    [],
  );

  const handleAcquirerNsuChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    const input = event.target;
    const value = sanitizeNumericValue(input.value);

    input.value = value;

    setValue('acquirerNsu', value, {
      shouldValidate: true,
    });
  };

  const handleAuthorizationCodeChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    const input = event.target;
    const value = sanitizeAlphaNumericValue(input.value);

    input.value = value;

    setValue('authorizationCode', value, {
      shouldValidate: true,
    });
  };

  const togglePayAfter = (): void => {
    setPayAfter((prevState) => !prevState);
  };

  const handleFiscalRecipientChange = (): void => {
    setFiscalRecipient(fiscalRecipient?.id !== user.id ? user : titularUser);
  };

  const handleConfirm: SubmitHandler<IBookingPaymentModalFormData> = (data) => {
    if (!showPaymentDetailsFields) {
      onConfirm(
        payAfter
          ? undefined
          : {
              userFiscalRecipient: true,
            },
      );
      return;
    }

    const {
      address,
      authorizationCode,
      authorizedAt,
      type,
      acquirerNsu,
      acquirerName,
      brand,
    } = data;

    onConfirm(
      payAfter
        ? undefined
        : {
            address,
            authorizationCode,
            authorizedAt,
            userFiscalRecipient: fiscalRecipient?.id === user.id,
            type,
            acquirerNsu,
            acquirerName,
            brand,
          },
    );
  };

  return (
    <Modal
      size="5xl"
      scrollBehavior="outside"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />

      <FormProvider {...formMethods}>
        <ModalContent as="form" onSubmit={handleSubmit(handleConfirm)}>
          <ModalHeader>
            <Heading size="lg" fontWeight="normal">
              Confirmação de pagamento
            </Heading>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody pb={isPixPayment ? '12' : '0'}>
            <Text mt="4" color="red.500">
              Em caso de cancelamento, o reembolso deverá ser feito
              externamente.
            </Text>

            {showPaymentDetailsFields &&
              (effectivePayAfterDisabled || payAfter) && (
                <Text mt="4" color="red.500">
                  Dados do pagamento deverão ser informados aqui para emissão da
                  nota fiscal.
                </Text>
              )}

            {!effectivePayAfterDisabled && (
              <HStack py="4">
                <Checkbox
                  colorScheme="blue"
                  borderColor="gray.300"
                  isChecked={payAfter}
                  onChange={togglePayAfter}
                />
                <Text>Pagar depois</Text>
              </HStack>
            )}

            {showPaymentDetailsFields && (
              <SimpleGrid
                mt={effectivePayAfterDisabled ? '8' : '0'}
                pb="8"
                minChildWidth="240px"
                spacing="8"
                w="100%"
              >
                <ReactSelect
                  isDisabled={payAfter}
                  isRequired
                  label="Tipo de pagamento"
                  name="type"
                  options={paymentTypeSelectOptions}
                  control={control}
                  error={errors.type}
                />

                {isCardPayment && (
                  <ReactSelect
                    isDisabled={payAfter}
                    isRequired={isCardPayment}
                    label="Adquirente"
                    name="acquirerName"
                    options={acquirerSelectOptions}
                    control={control}
                    error={errors.acquirerName}
                  />
                )}

                {isCardPayment && (
                  <MaskedInput
                    isDisabled={payAfter}
                    isRequired={isCardPayment}
                    label="NSU/CV"
                    error={errors.acquirerNsu}
                    {...register('acquirerNsu', {
                      onChange: handleAcquirerNsuChange,
                    })}
                  />
                )}

                {!isCashPayment && (
                  <MaskedInput
                    isDisabled={payAfter}
                    isRequired
                    label="Código de autorização (AUT)"
                    error={errors.authorizationCode}
                    {...register('authorizationCode', {
                      onChange: handleAuthorizationCodeChange,
                    })}
                  />
                )}

                {(isPixPayment || isCashPayment) && (
                  <DatePicker
                    isDisabled={payAfter}
                    isRequired
                    label="Data"
                    name="authorizedAt"
                    control={control}
                    error={errors.authorizedAt}
                  />
                )}

                {isCardPayment && (
                  <ReactSelect
                    isDisabled={payAfter}
                    isRequired={isCardPayment}
                    label="Bandeira do cartão"
                    name="brand"
                    options={cardBrandSelectOptions}
                    control={control}
                    error={errors.brand}
                  />
                )}
              </SimpleGrid>
            )}

            {showPaymentDetailsFields && !!fiscalRecipient && (
              <>
                <Divider my="6" borderColor="gray.300" />

                <FiscalRecipientInfo
                  fiscalRecipient={fiscalRecipient}
                  userFiscalRecipient={userFiscalRecipient}
                  onToggleFiscalRecipient={handleFiscalRecipientChange}
                  enableFiscalRecipientChange={isGuestUser}
                  hideFiscalRecipientCheckbox={isLodgerUser}
                />

                {!fiscalRecipient.address && (
                  <>
                    <Divider my="6" borderColor="gray.300" />

                    <AddressForm />
                  </>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter mt="8">
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={onClose}>
                Cancelar
              </Button>

              <Button colorScheme="green" type="submit">
                Confirmar
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormProvider>
    </Modal>
  );
};
